/**
 * User roles in the application
 */
export enum UserRole {
  User = "User",
  Preparer = "Preparer",
  Reviewer = "Reviewer",
  Partner = "Partner",
}

/**
 * Role hierarchy, lowest → highest. Used by `canHoldRole` / `isRoleBelow`
 * and by the Settings page's "allowed roles" dropdown so the rules around
 * who-can-be-what live in one place.
 */
export const ROLE_HIERARCHY: readonly UserRole[] = [
  UserRole.User,
  UserRole.Preparer,
  UserRole.Reviewer,
  UserRole.Partner,
];

/**
 * Get all available user roles as an array
 */
export const getAllUserRoles = (): UserRole[] => {
  return Object.values(UserRole);
};

/**
 * Default role assigned to a user who hasn't picked one yet (i.e. first
 * time opening the addin, no entry in localStorage). Preparer is the
 * realistic baseline for our user base — most staff using the addin are
 * preparing workpapers, not just observing.
 */
export const getDefaultUserRole = (): UserRole => {
  return UserRole.Preparer;
};

/**
 * True when the caller's maximum allowed role permits being at `target`.
 *
 * The `maxRole` value is the string returned by the backend's /me/role
 * endpoint (or a dev override). Backend values that aren't in our enum
 * — currently "Manager" — pass through as "unknown maximum" and we permit
 * everything, matching the Settings page's existing fall-through behaviour.
 * Null / undefined maxRole (e.g. still loading) is also treated as
 * "permit anything"; the Settings cap will catch over-promotion on the
 * next load if anything slips through.
 */
export const canHoldRole = (target: UserRole, maxRole: string | null | undefined): boolean => {
  if (!maxRole) return true;
  const maxIndex = ROLE_HIERARCHY.indexOf(maxRole as UserRole);
  if (maxIndex === -1) return true;
  return ROLE_HIERARCHY.indexOf(target) <= maxIndex;
};

/**
 * True when `current` is strictly below `target` in the role hierarchy.
 * Used by the Setup auto-promote flow ("only promote, never downgrade").
 */
export const isRoleBelow = (current: UserRole, target: UserRole): boolean => {
  return ROLE_HIERARCHY.indexOf(current) < ROLE_HIERARCHY.indexOf(target);
};

/**
 * Coerce a raw backend role string ("Partner" / "Manager" / "Reviewer" /
 * "Preparer" — case-insensitive) into a value from our UserRole enum.
 *
 * "Manager" is a backend-only distinction we don't model in the frontend —
 * it collapses to Reviewer, which is what Manager-permission users act as
 * inside the addin. Unrecognised values (or null/empty) return null so
 * callers can fall through to their own defaulting logic.
 *
 * Single source of truth for this mapping — used by the context's role
 * auto-init and the Settings page's role-cap logic.
 */
export const normaliseBackendRoleToUserRole = (role: string | null | undefined): UserRole | null => {
  if (!role) return null;
  switch (role.toLowerCase()) {
    case "partner": return UserRole.Partner;
    case "reviewer":
    case "manager": return UserRole.Reviewer;
    case "preparer": return UserRole.Preparer;
    default: return null;
  }
};
